"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_EVENT_DISPATCHER_CONFIG = exports.DEFAULT_EVENT_DISPATCHER_BATCH_SIZE = void 0;
exports.newDefaultEventDispatcher = newDefaultEventDispatcher;
const application_logger_1 = require("../application-logger");
const batch_event_processor_1 = require("./batch-event-processor");
const batch_retry_manager_1 = require("./batch-retry-manager");
const event_delivery_1 = require("./event-delivery");
const no_op_event_dispatcher_1 = require("./no-op-event-dispatcher");
const sdk_key_decoder_1 = require("./sdk-key-decoder");
const MAX_CONTEXT_SERIALIZED_LENGTH = 2048;
const MAX_EVENT_SERIALIZED_LENGTH = 4096;
exports.DEFAULT_EVENT_DISPATCHER_BATCH_SIZE = 1000;
exports.DEFAULT_EVENT_DISPATCHER_CONFIG = {
    deliveryIntervalMs: 10000,
    retryIntervalMs: 5000,
    maxRetryDelayMs: 30000,
    maxRetries: 3,
};
/**
 * @internal
 * An {@link EventDispatcher} that, given the provided config settings, delivers events in batches
 * to the ingestionUrl and retries failed deliveries. Also reacts to network status changes to
 * determine when to deliver events.
 */
class DefaultEventDispatcher {
    constructor(batchProcessor, networkStatusListener, config) {
        this.batchProcessor = batchProcessor;
        this.networkStatusListener = networkStatusListener;
        this.context = {};
        this.dispatchTimer = null;
        this.isOffline = false;
        this.ensureConfigFields(config);
        const { sdkKey, ingestionUrl, retryIntervalMs, maxRetryDelayMs, maxRetries = 3 } = config;
        this.eventDelivery = new event_delivery_1.default(sdkKey, ingestionUrl);
        this.retryManager = new batch_retry_manager_1.default(this.eventDelivery, {
            retryIntervalMs,
            maxRetryDelayMs,
            maxRetries,
        });
        this.deliveryIntervalMs = config.deliveryIntervalMs;
        this.networkStatusListener.onNetworkStatusChange((isOffline) => {
            application_logger_1.logger.info(`[EventDispatcher] Network status change, isOffline=${isOffline}.`);
            this.isOffline = isOffline;
            if (isOffline) {
                this.dispatchTimer = null;
            }
            else {
                this.maybeScheduleNextDelivery();
            }
        });
    }
    attachContext(key, value) {
        this.ensureValidContext(key, value);
        this.context[key] = value;
    }
    dispatch(event) {
        this.ensureValidEvent(event);
        this.batchProcessor.push(event);
        this.maybeScheduleNextDelivery();
    }
    ensureValidContext(key, value) {
        if (value && (typeof value === 'object' || Array.isArray(value))) {
            throw new Error('Context value must be a string, number, boolean, or null');
        }
        if (value &&
            JSON.stringify({ ...this.context, [key]: value }).length > MAX_CONTEXT_SERIALIZED_LENGTH) {
            throw new Error(`The total context size must be less than ${MAX_CONTEXT_SERIALIZED_LENGTH} characters`);
        }
    }
    ensureValidEvent(event) {
        if (JSON.stringify(event).length > MAX_EVENT_SERIALIZED_LENGTH) {
            throw new Error(`Event serialized length exceeds maximum allowed length of ${MAX_EVENT_SERIALIZED_LENGTH}`);
        }
    }
    async deliverNextBatch() {
        if (this.isOffline) {
            application_logger_1.logger.warn('[EventDispatcher] Skipping delivery; network status is offline.');
            return;
        }
        const batch = this.batchProcessor.nextBatch();
        if (batch.length === 0) {
            // nothing to deliver
            this.dispatchTimer = null;
            return;
        }
        // make a defensive copy of the context to avoid mutating the original
        const context = { ...this.context };
        const { failedEvents } = await this.eventDelivery.deliver(batch, context);
        if (failedEvents.length > 0) {
            application_logger_1.logger.warn('[EventDispatcher] Failed to deliver some events from batch, retrying...');
            const failedRetry = await this.retryManager.retry(failedEvents, context);
            if (failedRetry.length > 0) {
                // re-enqueue events that failed to retry
                this.batchProcessor.push(...failedRetry);
            }
        }
        application_logger_1.logger.debug(`[EventDispatcher] Delivered batch of ${batch.length} events.`);
        this.dispatchTimer = null;
        this.maybeScheduleNextDelivery();
    }
    maybeScheduleNextDelivery() {
        // schedule next event delivery when:
        // 1. we're not offline
        // 2. there are enqueued events
        // 3. there isn't already a scheduled delivery
        if (!this.isOffline && !this.batchProcessor.isEmpty() && !this.dispatchTimer) {
            application_logger_1.logger.info(`[EventDispatcher] Scheduling next delivery in ${this.deliveryIntervalMs}ms.`);
            this.dispatchTimer = setTimeout(() => this.deliverNextBatch(), this.deliveryIntervalMs);
        }
    }
    ensureConfigFields(config) {
        if (!config.ingestionUrl) {
            throw new Error('Missing required ingestionUrl in EventDispatcherConfig');
        }
        if (!config.deliveryIntervalMs) {
            throw new Error('Missing required deliveryIntervalMs in EventDispatcherConfig');
        }
        if (!config.retryIntervalMs) {
            throw new Error('Missing required retryIntervalMs in EventDispatcherConfig');
        }
        if (!config.maxRetryDelayMs) {
            throw new Error('Missing required maxRetryDelayMs in EventDispatcherConfig');
        }
    }
}
exports.default = DefaultEventDispatcher;
/** Creates a new {@link DefaultEventDispatcher} with the provided configuration. */
function newDefaultEventDispatcher(eventQueue, networkStatusListener, sdkKey, batchSize = exports.DEFAULT_EVENT_DISPATCHER_BATCH_SIZE, config = exports.DEFAULT_EVENT_DISPATCHER_CONFIG) {
    const sdkKeyDecoder = new sdk_key_decoder_1.default();
    const ingestionUrl = sdkKeyDecoder.decodeEventIngestionUrl(sdkKey);
    if (!ingestionUrl) {
        application_logger_1.logger.debug('Unable to parse Event ingestion URL from SDK key, falling back to no-op event dispatcher');
        return new no_op_event_dispatcher_1.default();
    }
    return new DefaultEventDispatcher(new batch_event_processor_1.default(eventQueue, batchSize), networkStatusListener, { ...config, ingestionUrl, sdkKey });
}
//# sourceMappingURL=default-event-dispatcher.js.map